import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { getPdfAssets, addLogo, drawCompanyBlock, applyWatermarkAllPages } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { FileText, Plus, Edit, Trash2, Download, AlertCircle } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../contexts/AuthContext";

const Prestadores = () => {
  const { isAdmin } = useAuth();
  const [prestadores, setPrestadores] = useState([]);
  const [confirm, confirmDialog] = useConfirm();
  const [loading, setLoading] = useState(true);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroNumero, setFiltroNumero] = useState(''); // Filtro por matrícula (ex: P-000113)
  const [filtroFuncao, setFiltroFuncao] = useState('todas'); // Novo filtro por função
  const [filtroStatus, setFiltroStatus] = useState('todos'); // Filtro por status (ativo/inativo)
  const [filtroCadastro, setFiltroCadastro] = useState('todos'); // Filtro por completude do cadastro
  const [funcoesPrestador, setFuncoesPrestador] = useState([]); // Lista de funções disponíveis
  const [cpfError, setCpfError] = useState(''); // Erro de CPF duplicado
  const [buscandoCep, setBuscandoCep] = useState(false); // Loading da busca de CEP
  const [camposComErro, setCamposComErro] = useState({}); // Erros de validação por campo
  const [tentouSalvar, setTentouSalvar] = useState(false); // Flag para mostrar erros apenas após tentar salvar
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    data_nascimento: '',
    cnpj_mei: '',
    rg: '',
    rg_data_expedicao: '',
    pis: '',
    email: '',
    telefone: '',
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    filiacao_pai: '',
    filiacao_mae: '',
    funcoes: [], // Novo campo para funções
    chave_pix: '',
    forma_pagamento: '',
    observacoes: '',
    status: 'ativo'
  });
  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchPrestadores = async () => {
    try {
      const [prestadoresRes, funcoesRes] = await Promise.all([
        axios.get(`${API}/prestadores`),
        axios.get(`${API}/funcoes-prestador`)
      ]);
      // Ordenar prestadores alfabeticamente por nome
      const prestadoresOrdenados = prestadoresRes.data.sort(sortByName);
      setPrestadores(prestadoresOrdenados);
      setFuncoesPrestador(funcoesRes.data.funcoes || []);
    } catch (error) {
      toast.error("Erro ao carregar prestadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchPrestadores(); };
    carregar();
  }, []);

  // Função para buscar endereço pelo CEP via ViaCEP
  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));
      
      if (data.logradouro) {
        toast.success("Endereço preenchido automaticamente!");
      }
    } catch (error) {
      toast.error("Erro ao buscar CEP. Preencha manualmente.");
    } finally {
      setBuscandoCep(false);
    }
  };

  // Função para verificar CPF duplicado
  const verificarCpfDuplicado = async (cpf) => {
    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length < 11) {
      setCpfError('');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/prestadores/verificar-cpf/${cpfLimpo}`);
      if (response.data.existe && response.data.prestador_id !== editingId) {
        setCpfError(`CPF já cadastrado para: ${response.data.nome}`);
      } else {
        setCpfError('');
      }
    } catch (error) {
      // Verificação best-effort; em caso de falha apenas registra e segue
      console.warn('Falha ao verificar CPF duplicado:', error);
    }
  };

  // Retorna a lista de campos obrigatórios faltantes do prestador (mesmos campos do backend)
  const camposFaltantesPrestador = (p) => {
    const faltaTexto = (v) => !v || !String(v).trim();
    const faltantes = [];
    if (faltaTexto(p.nome)) faltantes.push('Nome');
    if (faltaTexto(p.cpf_cnpj)) faltantes.push('CPF');
    if (faltaTexto(p.data_nascimento)) faltantes.push('Data de Nascimento');
    if (faltaTexto(p.filiacao_pai)) faltantes.push('Nome do Pai');
    if (faltaTexto(p.filiacao_mae)) faltantes.push('Nome da Mãe');
    if (!p.funcoes || p.funcoes.length === 0) faltantes.push('Funções');
    if (faltaTexto(p.cep)) faltantes.push('CEP');
    if (faltaTexto(p.endereco)) faltantes.push('Logradouro');
    if (faltaTexto(p.numero)) faltantes.push('Número');
    if (faltaTexto(p.bairro)) faltantes.push('Bairro');
    if (faltaTexto(p.cidade)) faltantes.push('Cidade');
    if (faltaTexto(p.estado)) faltantes.push('Estado');
    if (faltaTexto(p.email)) faltantes.push('E-mail');
    if (faltaTexto(p.telefone)) faltantes.push('Telefone');
    if (faltaTexto(p.chave_pix)) faltantes.push('Chave PIX');
    if (faltaTexto(p.forma_pagamento)) faltantes.push('Forma de Pagamento');
    return faltantes;
  };

  // Verifica se o cadastro do prestador está incompleto (mesmos campos obrigatórios do backend)
  const prestadorIncompleto = (p) => camposFaltantesPrestador(p).length > 0;

  // Função para filtrar prestadores por nome, função e status
  const getPrestadoresFiltrados = () => {
    return prestadores.filter(prestador => {
      // Filtro por nome
      if (filtroNome && !prestador.nome.toLowerCase().includes(filtroNome.toLowerCase())) {
        return false;
      }

      // Filtro por matrícula / número de cadastro (ex: P-000113)
      if (filtroNumero && !(prestador.numero_cadastro || '').toLowerCase().includes(filtroNumero.toLowerCase().trim())) {
        return false;
      }
      
      // Filtro por função
      if (filtroFuncao !== 'todas') {
        const prestadorFuncoes = prestador.funcoes || [];
        if (!prestadorFuncoes.includes(filtroFuncao)) {
          return false;
        }
      }
      
      // Filtro por status
      if (filtroStatus !== 'todos') {
        if (prestador.status !== filtroStatus) {
          return false;
        }
      }

      // Filtro por completude do cadastro
      if (filtroCadastro !== 'todos') {
        const incompleto = prestadorIncompleto(prestador);
        if (filtroCadastro === 'incompleto' && !incompleto) return false;
        if (filtroCadastro === 'completo' && incompleto) return false;
      }
      
      return true;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTentouSalvar(true);
    
    // Validação de campos obrigatórios no frontend
    const erros = {};
    const camposFaltando = [];
    
    // Dados Pessoais
    if (!formData.nome || !formData.nome.trim()) {
      erros.nome = 'Este campo é obrigatório';
      camposFaltando.push('Nome Completo');
    }
    if (!formData.cpf_cnpj || !formData.cpf_cnpj.trim()) {
      erros.cpf_cnpj = 'Este campo é obrigatório';
      camposFaltando.push('CPF');
    }
    if (!formData.data_nascimento) {
      erros.data_nascimento = 'Este campo é obrigatório';
      camposFaltando.push('Data de Nascimento');
    }
    if (!formData.filiacao_pai || !formData.filiacao_pai.trim()) {
      erros.filiacao_pai = 'Este campo é obrigatório';
      camposFaltando.push('Nome do Pai');
    }
    if (!formData.filiacao_mae || !formData.filiacao_mae.trim()) {
      erros.filiacao_mae = 'Este campo é obrigatório';
      camposFaltando.push('Nome da Mãe');
    }
    
    // Funções
    if (!formData.funcoes || formData.funcoes.length === 0) {
      erros.funcoes = 'Selecione ao menos uma função';
      camposFaltando.push('Funções');
    }
    
    // Endereço
    if (!formData.cep || !formData.cep.trim()) {
      erros.cep = 'Este campo é obrigatório';
      camposFaltando.push('CEP');
    }
    if (!formData.endereco || !formData.endereco.trim()) {
      erros.endereco = 'Este campo é obrigatório';
      camposFaltando.push('Logradouro');
    }
    if (!formData.numero || !formData.numero.trim()) {
      erros.numero = 'Este campo é obrigatório';
      camposFaltando.push('Número');
    }
    if (!formData.bairro || !formData.bairro.trim()) {
      erros.bairro = 'Este campo é obrigatório';
      camposFaltando.push('Bairro');
    }
    if (!formData.cidade || !formData.cidade.trim()) {
      erros.cidade = 'Este campo é obrigatório';
      camposFaltando.push('Cidade');
    }
    if (!formData.estado || !formData.estado.trim()) {
      erros.estado = 'Este campo é obrigatório';
      camposFaltando.push('Estado');
    }
    
    // Contato e Pagamento
    if (!formData.email || !formData.email.trim()) {
      erros.email = 'Este campo é obrigatório';
      camposFaltando.push('E-mail');
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      erros.email = 'Formato de e-mail inválido';
      camposFaltando.push('E-mail (formato inválido)');
    }
    if (!formData.telefone || !formData.telefone.trim()) {
      erros.telefone = 'Este campo é obrigatório';
      camposFaltando.push('Telefone');
    } else if (formData.telefone.replace(/\D/g, '').length < 10) {
      erros.telefone = 'Mínimo 10 dígitos';
      camposFaltando.push('Telefone (mínimo 10 dígitos)');
    }
    if (!formData.chave_pix || !formData.chave_pix.trim()) {
      erros.chave_pix = 'Este campo é obrigatório';
      camposFaltando.push('Chave PIX');
    }
    if (!formData.forma_pagamento || !formData.forma_pagamento.trim()) {
      erros.forma_pagamento = 'Este campo é obrigatório';
      camposFaltando.push('Forma de Pagamento');
    }
    
    setCamposComErro(erros);
    
    // Se houver erros, mostrar mensagem e não salvar
    if (Object.keys(erros).length > 0) {
      toast.error(`Existem campos obrigatórios não preenchidos. Verifique os campos destacados em vermelho.`);
      return;
    }
    
    // Verificar se há erro de CPF duplicado
    if (cpfError) {
      toast.error(cpfError);
      return;
    }
    
    try {
      // Preparar dados para envio, convertendo strings vazias de data para null
      const dataToSend = {
        ...formData,
        data_nascimento: formData.data_nascimento || null,
        rg_data_expedicao: formData.rg_data_expedicao || null
      };
      
      if (editingId) {
        await axios.put(`${API}/prestadores/${editingId}`, dataToSend);
        toast.success("Prestador atualizado com sucesso!");
      } else {
        await axios.post(`${API}/prestadores`, dataToSend);
        toast.success("Prestador criado com sucesso!");
      }
      
      setFormData({
        nome: '',
        cpf_cnpj: '',
        data_nascimento: '',
        cnpj_mei: '',
        rg: '',
        rg_data_expedicao: '',
        pis: '',
        email: '',
        telefone: '',
        cep: '',
        endereco: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        filiacao_pai: '',
        filiacao_mae: '',
        funcoes: [],
        chave_pix: '',
        forma_pagamento: '',
        observacoes: '',
        status: 'ativo'
      });
      setEditingId(null);
      setIsDialogOpen(false);
      setCpfError('');
      setCamposComErro({});
      setTentouSalvar(false);
      fetchPrestadores();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao salvar prestador";
      toast.error(errorMsg);
    }
  };

  const handleEdit = (prestador) => {
    setCpfError('');
    setCamposComErro({});
    setTentouSalvar(false);
    setFormData({
      nome: prestador.nome,
      cpf_cnpj: prestador.cpf_cnpj,
      data_nascimento: prestador.data_nascimento || '',
      cnpj_mei: prestador.cnpj_mei || '',
      rg: prestador.rg || '',
      rg_data_expedicao: prestador.rg_data_expedicao || '',
      pis: prestador.pis || '',
      email: prestador.email || '',
      telefone: prestador.telefone || '',
      cep: prestador.cep || '',
      endereco: prestador.endereco || '',
      numero: prestador.numero || '',
      bairro: prestador.bairro || '',
      cidade: prestador.cidade || '',
      estado: prestador.estado || '',
      filiacao_pai: prestador.filiacao_pai || '',
      filiacao_mae: prestador.filiacao_mae || '',
      funcoes: prestador.funcoes || [],
      chave_pix: prestador.chave_pix || '',
      forma_pagamento: prestador.forma_pagamento || '',
      observacoes: prestador.observacoes || '',
      status: prestador.status
    });
    setEditingId(prestador.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Excluir prestador?",
      description: "Tem certeza que deseja deletar este prestador? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/prestadores/${id}`);
      toast.success("Prestador deletado com sucesso!");
      fetchPrestadores();
    } catch (error) {
      toast.error("Erro ao deletar prestador");
    }
  };

  // Função para exportar prestadores para Excel (.xlsx)
  const exportarPrestadoresExcel = () => {
    const prestadoresFiltrados = getPrestadoresFiltrados();

    if (prestadoresFiltrados.length === 0) {
      toast.error("Nenhum prestador para exportar");
      return;
    }

    const rows = prestadoresFiltrados.map(p => ({
      'Cadastro': p.numero_cadastro || '',
      'Nome': p.nome || '',
      'CPF': p.cpf_cnpj || '',
      'Data Nascimento': p.data_nascimento ? new Date(p.data_nascimento + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'RG': p.rg || '',
      'Data Expedição RG': p.rg_data_expedicao ? new Date(p.rg_data_expedicao + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'PIS': p.pis || '',
      'CNPJ MEI': p.cnpj_mei || '',
      'Email': p.email || '',
      'Telefone': p.telefone || '',
      'CEP': p.cep || '',
      'Endereço': p.endereco || '',
      'Bairro': p.bairro || '',
      'Cidade': p.cidade || '',
      'Estado': p.estado || '',
      'Pai': p.filiacao_pai || '',
      'Mãe': p.filiacao_mae || '',
      'Funções': p.funcoes && p.funcoes.length > 0 ? p.funcoes.join('; ') : '',
      'Chave PIX': p.chave_pix || '',
      'Forma Pagamento': p.forma_pagamento || '',
      'Status': p.status || '',
      'Observações': p.observacoes || '',
    }));

    exportToExcel(rows, `prestadores-${new Date().toISOString().split('T')[0]}`, 'Prestadores');
    toast.success("Prestadores exportados para Excel com sucesso!");
  };

  // Função para exportar APENAS os prestadores com cadastro incompleto (com os campos que faltam)
  const exportarIncompletosExcel = () => {
    const incompletos = prestadores.filter(p => prestadorIncompleto(p));

    if (incompletos.length === 0) {
      toast.success("Todos os prestadores estão com cadastro completo!");
      return;
    }

    const rows = incompletos.map(p => {
      const faltantes = camposFaltantesPrestador(p);
      return {
        'Cadastro': p.numero_cadastro || '',
        'Nome': p.nome || '',
        'Status': p.status || '',
        'Qtd Campos Faltantes': faltantes.length,
        'Campos Faltantes': faltantes.join('; '),
      };
    });

    exportToExcel(rows, `prestadores-incompletos-${new Date().toISOString().split('T')[0]}`, 'Incompletos');
    toast.success(`${incompletos.length} prestador(es) incompleto(s) exportado(s)!`);
  };

  // Função para exportar prestadores para PDF
  const exportarPrestadoresPDF = async () => {
    const prestadoresFiltrados = getPrestadoresFiltrados();
    
    if (prestadoresFiltrados.length === 0) {
      toast.error("Nenhum prestador para exportar");
      return;
    }

    try {
      const { logoDataUrl, empresa } = await getPdfAssets();
      const doc = new jsPDF();
      
      // Logo + dados da empresa
      addLogo(doc, logoDataUrl, 10, 10, 38, 19);
      drawCompanyBlock(doc, empresa, 52, 14);
      
      // Cabeçalho
      doc.setFillColor(155, 118, 23);
      doc.rect(10, 35, 190, 10, 'F');
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('LISTA DE PRESTADORES', 15, 42);
      doc.text(`Total: ${prestadoresFiltrados.length}`, 160, 42);
      
      let yPosition = 52;
      doc.setTextColor(0, 0, 0);
      
      prestadoresFiltrados.forEach((p, index) => {
        // Verificar nova página
        if (yPosition > 250) {
          doc.addPage();
          addLogo(doc, logoDataUrl, 10, 10, 38, 19);
          drawCompanyBlock(doc, empresa, 52, 14);
          doc.setFillColor(155, 118, 23);
          doc.rect(10, 35, 190, 10, 'F');
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text('LISTA DE PRESTADORES', 15, 42);
          yPosition = 52;
          doc.setTextColor(0, 0, 0);
        }
        
        // Número de cadastro
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(155, 118, 23); // Dourado/bronze
        doc.text(`${p.numero_cadastro || 'SEM CADASTRO'}`, 15, yPosition);
        doc.setTextColor(0, 0, 0);
        
        // Nome
        doc.setFontSize(11);
        doc.text(` - ${p.nome || 'Nome não informado'}`, 35, yPosition);
        yPosition += 7;
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // CPF
        doc.text(`CPF: ${p.cpf_cnpj || 'Não informado'}`, 15, yPosition);
        yPosition += 5;
        
        // Data de Nascimento
        if (p.data_nascimento) {
          const dataNasc = new Date(p.data_nascimento + 'T00:00:00').toLocaleDateString('pt-BR');
          doc.text(`Data Nascimento: ${dataNasc}`, 15, yPosition);
          yPosition += 5;
        }
        
        // RG e Data Expedição
        doc.text(`RG: ${p.rg || 'Não informado'}`, 15, yPosition);
        if (p.rg_data_expedicao) {
          const dataExpRG = new Date(p.rg_data_expedicao + 'T00:00:00').toLocaleDateString('pt-BR');
          doc.text(`Expedição: ${dataExpRG}`, 80, yPosition);
        }
        yPosition += 5;
        
        // PIS
        doc.text(`PIS: ${p.pis || 'Não informado'}`, 15, yPosition);
        yPosition += 5;
        
        // CNPJ MEI
        if (p.cnpj_mei) {
          doc.text(`CNPJ MEI: ${p.cnpj_mei}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Email e Telefone
        if (p.email || p.telefone) {
          doc.text(`Email: ${p.email || '-'}`, 15, yPosition);
          doc.text(`Telefone: ${p.telefone || '-'}`, 100, yPosition);
          yPosition += 5;
        }
        
        // Endereço completo
        if (p.endereco || p.bairro || p.cidade) {
          const endereco = `${p.endereco || ''}${p.numero ? ', ' + p.numero : ''}${p.bairro ? ' - ' + p.bairro : ''}`;
          const cidadeEstado = `${p.cidade || ''}${p.estado ? ' - ' + p.estado : ''}`;
          doc.text(`Endereço: ${endereco}`, 15, yPosition);
          yPosition += 5;
          if (cidadeEstado.trim()) {
            doc.text(`Cidade: ${cidadeEstado}`, 15, yPosition);
            yPosition += 5;
          }
        }
        
        // CEP
        if (p.cep) {
          doc.text(`CEP: ${p.cep}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Filiação
        if (p.filiacao_pai) {
          doc.text(`Pai: ${p.filiacao_pai}`, 15, yPosition);
          yPosition += 5;
        }
        if (p.filiacao_mae) {
          doc.text(`Mãe: ${p.filiacao_mae}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Funções
        if (p.funcoes && p.funcoes.length > 0) {
          doc.text(`Funções: ${p.funcoes.join(', ')}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Chave PIX
        if (p.chave_pix) {
          doc.text(`Chave PIX: ${p.chave_pix}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Status
        doc.text(`Status: ${p.status.toUpperCase()}`, 15, yPosition);
        yPosition += 3;
        
        // Linha separadora
        doc.setLineWidth(0.3);
        doc.setDrawColor(200, 200, 200);
        doc.line(10, yPosition, 200, yPosition);
        yPosition += 8;
      });
      
      // Salvar PDF usando Blob para maior compatibilidade
      applyWatermarkAllPages(doc, logoDataUrl);
      const filename = `prestadores-${new Date().toISOString().split('T')[0]}`;
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Prestadores exportados para PDF com sucesso!");
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error("Erro ao gerar PDF: " + error.message);
    }
  };


  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Prestadores</h1>
          <p className="text-gray-600 dark:text-gray-300">Gerencie os prestadores de serviços com dados para INSS</p>
        </div>
        
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={exportarPrestadoresPDF}>
            <FileText className="mr-2 h-4 w-4" />
            Exportar PDF
          </Button>
          <Button variant="outline" onClick={exportarPrestadoresExcel} data-testid="exportar-prestadores-excel-btn">
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>

          <Button variant="outline" onClick={exportarIncompletosExcel} data-testid="exportar-incompletos-btn" className="text-amber-700 dark:text-amber-400 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40">
            <AlertCircle className="mr-2 h-4 w-4" />
            Exportar incompletos
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
              setCpfError('');
              setCamposComErro({});
              setTentouSalvar(false);
              setFormData({
                nome: '',
                cpf_cnpj: '',
                data_nascimento: '',
                cnpj_mei: '',
                rg: '',
                rg_data_expedicao: '',
                pis: '',
                email: '',
                telefone: '',
                cep: '',
                endereco: '',
                numero: '',
                bairro: '',
                cidade: '',
                estado: '',
                filiacao_pai: '',
                filiacao_mae: '',
                funcoes: [],
                chave_pix: '',
                forma_pagamento: '',
                observacoes: '',
                status: 'ativo'
              });
              setEditingId(null);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Prestador
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar' : 'Novo'} Prestador</DialogTitle>
              <DialogDescription>
                Preencha os dados do prestador incluindo informações para INSS.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 [&_input]:uppercase [&_textarea]:uppercase">
              {/* Dados Pessoais */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Dados Pessoais</h3>
                
                {/* Mostrar número de cadastro quando estiver editando */}
                {editingId && (
                  <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-md p-3 mb-4">
                    <Label className="text-amber-700 dark:text-amber-400">Número de Cadastro (Matrícula)</Label>
                    <p className="text-lg font-mono font-bold text-amber-800 dark:text-amber-300">
                      {prestadores.find(p => p.id === editingId)?.numero_cadastro || 'Gerando...'}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Este número é gerado automaticamente e não pode ser alterado.</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome Completo</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className={tentouSalvar && camposComErro.nome ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.nome && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.nome}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj">CPF</Label>
                    <Input
                      id="cpf_cnpj"
                      value={formData.cpf_cnpj}
                      onChange={(e) => {
                        setFormData({...formData, cpf_cnpj: e.target.value});
                        verificarCpfDuplicado(e.target.value);
                      }}
                      placeholder="000.000.000-00"
                      className={(tentouSalvar && camposComErro.cpf_cnpj) || cpfError ? 'border-red-500' : ''}
                    />
                    {cpfError && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{cpfError}</p>
                    )}
                    {tentouSalvar && camposComErro.cpf_cnpj && !cpfError && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.cpf_cnpj}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                    <DatePicker
                      id="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={(value) => setFormData({...formData, data_nascimento: value})}
                      placeholder="Selecione a data"
                      className={tentouSalvar && camposComErro.data_nascimento ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.data_nascimento && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.data_nascimento}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="rg">RG</Label>
                    <Input
                      id="rg"
                      value={formData.rg}
                      onChange={(e) => setFormData({...formData, rg: e.target.value})}
                      placeholder="00.000.000-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rg_data_expedicao">Data Expedição RG</Label>
                    <DatePicker
                      id="rg_data_expedicao"
                      value={formData.rg_data_expedicao}
                      onChange={(value) => setFormData({...formData, rg_data_expedicao: value})}
                      placeholder="Selecione a data"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pis">PIS</Label>
                    <Input
                      id="pis"
                      value={formData.pis}
                      onChange={(e) => setFormData({...formData, pis: e.target.value})}
                      placeholder="000.00000.00-0"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="cnpj_mei">CNPJ (MEI)</Label>
                  <Input
                    id="cnpj_mei"
                    value={formData.cnpj_mei}
                    onChange={(e) => setFormData({...formData, cnpj_mei: e.target.value})}
                    placeholder="00.000.000/0001-00"
                  />
                </div>
              </div>

              {/* Filiação */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Filiação</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="filiacao_pai">Nome do Pai</Label>
                    <Input
                      id="filiacao_pai"
                      value={formData.filiacao_pai}
                      onChange={(e) => setFormData({...formData, filiacao_pai: e.target.value})}
                      className={tentouSalvar && camposComErro.filiacao_pai ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.filiacao_pai && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.filiacao_pai}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="filiacao_mae">Nome da Mãe</Label>
                    <Input
                      id="filiacao_mae"
                      value={formData.filiacao_mae}
                      onChange={(e) => setFormData({...formData, filiacao_mae: e.target.value})}
                      className={tentouSalvar && camposComErro.filiacao_mae ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.filiacao_mae && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.filiacao_mae}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Funções */}
              <div className="space-y-4">
                <h3 className={`text-lg font-medium border-b pb-2 ${tentouSalvar && camposComErro.funcoes ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  Funções que o Prestador Exerce
                </h3>
                {tentouSalvar && camposComErro.funcoes && (
                  <p className="text-red-500 dark:text-red-400 text-xs">{camposComErro.funcoes}</p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {funcoesPrestador.map((funcao) => (
                    <div key={funcao} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`funcao-${funcao}`}
                        checked={formData.funcoes.includes(funcao)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, funcoes: [...formData.funcoes, funcao]});
                          } else {
                            setFormData({...formData, funcoes: formData.funcoes.filter(f => f !== funcao)});
                          }
                        }}
                        className="w-4 h-4 text-amber-700 dark:text-amber-400 rounded focus:ring-amber-500"
                      />
                      <Label htmlFor={`funcao-${funcao}`} className="text-sm cursor-pointer">
                        {funcao}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Endereço */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Endereço</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="cep">CEP</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cep"
                        value={formData.cep}
                        onChange={(e) => {
                          const cep = e.target.value;
                          setFormData({...formData, cep});
                          if (cep.replace(/\D/g, '').length === 8) {
                            buscarCep(cep);
                          }
                        }}
                        placeholder="00000-000"
                        className={`flex-1 ${tentouSalvar && camposComErro.cep ? 'border-red-500' : ''}`}
                      />
                      {buscandoCep && (
                        <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          Buscando...
                        </span>
                      )}
                    </div>
                    {tentouSalvar && camposComErro.cep && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.cep}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="endereco">Logradouro</Label>
                    <Input
                      id="endereco"
                      value={formData.endereco}
                      onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                      placeholder="Rua, Avenida..."
                      className={tentouSalvar && camposComErro.endereco ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.endereco && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.endereco}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={formData.numero}
                      onChange={(e) => setFormData({...formData, numero: e.target.value})}
                      placeholder="123"
                      className={tentouSalvar && camposComErro.numero ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.numero && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.numero}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={formData.bairro}
                      onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                      placeholder="Nome do bairro"
                      className={tentouSalvar && camposComErro.bairro ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.bairro && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.bairro}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={formData.cidade}
                      onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                      className={tentouSalvar && camposComErro.cidade ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.cidade && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.cidade}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="estado">Estado</Label>
                    <Input
                      id="estado"
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value})}
                      placeholder="SP, RJ, MG..."
                      maxLength={2}
                      className={tentouSalvar && camposComErro.estado ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.estado && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.estado}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Contato</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`!normal-case ${tentouSalvar && camposComErro.email ? 'border-red-500' : ''}`}
                    />
                    {tentouSalvar && camposComErro.email && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.email}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      value={formData.telefone}
                      onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                      placeholder="(11) 99999-9999"
                      className={tentouSalvar && camposComErro.telefone ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.telefone && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.telefone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pagamento */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Dados de Pagamento</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="chave_pix">Chave PIX</Label>
                    <Input
                      id="chave_pix"
                      value={formData.chave_pix}
                      onChange={(e) => setFormData({...formData, chave_pix: e.target.value})}
                      className={`!normal-case ${tentouSalvar && camposComErro.chave_pix ? 'border-red-500' : ''}`}
                    />
                    {tentouSalvar && camposComErro.chave_pix && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.chave_pix}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                    <Input
                      id="forma_pagamento"
                      value={formData.forma_pagamento}
                      onChange={(e) => setFormData({...formData, forma_pagamento: e.target.value})}
                      placeholder="PIX, Conta Corrente, etc."
                      className={tentouSalvar && camposComErro.forma_pagamento ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.forma_pagamento && (
                      <p className="text-red-500 dark:text-red-400 text-xs mt-1">{camposComErro.forma_pagamento}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Outros */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="no-print">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="filtro-nome">Buscar por Nome</Label>
              <Input
                id="filtro-nome"
                type="text"
                placeholder="Digite o nome do prestador..."
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filtro-numero">Buscar por Matrícula</Label>
              <Input
                id="filtro-numero"
                data-testid="filtro-numero-input"
                type="text"
                placeholder="Ex: P-000113"
                value={filtroNumero}
                onChange={(e) => setFiltroNumero(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filtro-funcao">Filtrar por Função</Label>
              <Select value={filtroFuncao} onValueChange={setFiltroFuncao}>
                <SelectTrigger id="filtro-funcao">
                  <SelectValue placeholder="Todas as funções" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as funções</SelectItem>
                  {funcoesPrestador.map((funcao) => (
                    <SelectItem key={funcao} value={funcao}>{funcao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="filtro-status">Filtrar por Status</Label>
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger id="filtro-status">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativo">Ativos</SelectItem>
                  <SelectItem value="inativo">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="filtro-cadastro">Filtrar por Cadastro</Label>
              <Select value={filtroCadastro} onValueChange={setFiltroCadastro}>
                <SelectTrigger id="filtro-cadastro" data-testid="filtro-cadastro">
                  <SelectValue placeholder="Todos os cadastros" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="completo">Cadastro completo</SelectItem>
                  <SelectItem value="incompleto">Cadastro incompleto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setFiltroNome('');
                setFiltroNumero('');
                setFiltroFuncao('todas');
                setFiltroStatus('todos');
                setFiltroCadastro('todos');
              }}
            >
              Limpar Filtros
            </Button>
            {(filtroNome || filtroNumero || filtroFuncao !== 'todas' || filtroStatus !== 'todos' || filtroCadastro !== 'todos') && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Mostrando {getPrestadoresFiltrados().length} de {prestadores.length} prestadores
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer hover:bg-gray-100 dark:hover:bg-muted" onClick={() => {
                    const sorted = [...prestadores].sort((a, b) => (a.numero_cadastro || '').localeCompare(b.numero_cadastro || ''));
                    setPrestadores(sorted);
                  }}>Cadastro ↕</TableHead>
                  <TableHead className="cursor-pointer hover:bg-gray-100 dark:hover:bg-muted" onClick={() => {
                    const sorted = [...prestadores].sort((a, b) => a.nome.localeCompare(b.nome));
                    setPrestadores(sorted);
                  }}>Nome ↕</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Tel</TableHead>
                  <TableHead>Funções</TableHead>
                  <TableHead>PIX</TableHead>
                  <TableHead>Bairro</TableHead>
                  <TableHead className="cursor-pointer hover:bg-gray-100 dark:hover:bg-muted" onClick={() => {
                    const sorted = [...prestadores].sort((a, b) => (a.cidade || '').localeCompare(b.cidade || ''));
                    setPrestadores(sorted);
                  }}>Cidade ↕</TableHead>
                  <TableHead className="no-print">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getPrestadoresFiltrados().map((prestador) => {
                  // Formatação compacta das funções
                  const funcoes = prestador.funcoes || [];
                  const funcoesDisplay = funcoes.length > 2 
                    ? `${funcoes.slice(0, 2).join(', ')} +${funcoes.length - 2}`
                    : funcoes.join(', ') || '-';
                  
                  // Formatação do telefone
                  const telefone = prestador.telefone || '-';
                  
                  // Formatação do PIX (resumir se muito longo)
                  const pix = prestador.chave_pix || '-';
                  const pixDisplay = pix.length > 15 ? `${pix.substring(0, 15)}...` : pix;
                  
                  return (
                  <TableRow key={prestador.id}>
                    <TableCell className="font-mono text-sm text-amber-700 dark:text-amber-400">{prestador.numero_cadastro || '-'}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{prestador.nome}</span>
                        {prestadorIncompleto(prestador) && (
                          <span
                            data-testid="badge-cadastro-incompleto"
                            title="Faltam dados obrigatórios neste cadastro"
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-medium px-2 py-0.5 whitespace-nowrap"
                          >
                            <AlertCircle className="h-3 w-3" />
                            Incompleto
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{prestador.cpf_cnpj || '-'}</TableCell>
                    <TableCell>{telefone}</TableCell>
                    <TableCell>
                      <span 
                        className="text-sm" 
                        title={funcoes.join(', ')}
                      >
                        {funcoesDisplay}
                      </span>
                    </TableCell>
                    <TableCell>
                      {pix !== '-' ? (
                        <span 
                          className="text-sm cursor-pointer hover:text-amber-700 dark:hover:text-amber-400" 
                          title={`Clique para copiar: ${pix}`}
                          onClick={() => {
                            navigator.clipboard.writeText(pix);
                            toast.success('PIX copiado!');
                          }}
                        >
                          {pixDisplay}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>{prestador.bairro || '-'}</TableCell>
                    <TableCell>{prestador.cidade || '-'}</TableCell>
                    <TableCell className="no-print">
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(prestador)} data-testid={`editar-prestador-${prestador.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isAdmin() && (
                          <Button size="sm" variant="outline" onClick={() => handleDelete(prestador.id)} data-testid={`excluir-prestador-${prestador.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Prestadores;
